/** @shell standard @header bar 
 * Order flow: Customer Details step. Select delivery address, see delivery cost, then Proceed to Pay -> OrderCheckout.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuthNavigation } from '../shared/navigation/useAuthNavigation';
import { useRequireOnlineOrdersEnabled } from '../shared/navigation/useFeatureGateExit';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import Animated from 'react-native-reanimated';

import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { appTheme, useThemeColors, useThemeStore, type ThemeColors } from '../shared/theme';
import { useTranslation, getApiText } from '../shared/i18n';
import { trailingRowDisclosureChevron } from '../shared/i18n/rtl';
import { useAddresses, useAreas } from '../data/domains/addresses';
import { getBookMealData } from '../data/domains/orders';
import { formatCurrency } from '../shared/utils/currencyUtils';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { androidAppHeaderContentTop } from '../shared/theme/sharedStyles';
import { hexToRgba } from '../shared/theme/colorUtils';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioCardShell,
  DASH_STUDIO_RADIUS,
  studioSectionLabelStyle,
} from '../shared/theme/studio/stackScreenShell';

function createOrderCustomerDetailsStyles(c: ThemeColors) {
  return StyleSheet.create({
    center: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: STACK_SCROLL_GUTTER },
    scroll: { flex: 1 },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      gap: STACK_SECTION_GAP,
    },
    bottomSpacer: { height: 100 },
    sectionTitle: studioSectionLabelStyle(c),
    addressCardWrap: {},
    addressCard: {
      flexDirection: 'row',
      alignItems: 'center',
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.md,
    },
    addressCardSelected: {
      backgroundColor: hexToRgba(c.primary, 0.08),
    },
    radioOuter: {
      width: 22,
      height: 22,
      borderRadius: 11,
      borderWidth: 2,
      borderColor: c.border,
      marginRight: appTheme.spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
    },
    radioOuterSelected: { borderColor: c.primary },
    radioInner: {
      width: 12,
      height: 12,
      borderRadius: 6,
      backgroundColor: c.primary,
    },
    addressInfo: { flex: 1, marginStart: appTheme.spacing.sm },
    addressName: { ...appTheme.typography.body, color: c.text, fontWeight: '600' },
    addressSub: { ...appTheme.typography.bodySmall, color: c.textSecondary, marginTop: 2 },
    summary: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.md,
    },
    summaryRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: appTheme.spacing.sm },
    summaryLabel: { ...appTheme.typography.body, color: c.textSecondary },
    summaryValue: { ...appTheme.typography.body, color: c.text },
    totalLabel: { ...appTheme.typography.body, color: c.text, fontWeight: '600' },
    totalValue: { ...appTheme.typography.h3, color: c.primary },
    emptyText: { ...appTheme.typography.body, color: c.textSecondary, marginBottom: 16 },
    primaryBtnWrap: {},
    primaryBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: c.primary,
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.xl,
      borderRadius: appTheme.borderRadius.md,
    },
    primaryBtnDisabled: { backgroundColor: c.borderLight },
    primaryBtnText: { ...appTheme.typography.body, color: c.onPrimary, fontWeight: '600' },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: appTheme.spacing.md,
      backgroundColor: c.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.border,
    },
    footerTotal: { ...appTheme.typography.h3, color: c.primary },
  });
}

export const OrderCustomerDetailsScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const online_orders_enabled = useRequireOnlineOrdersEnabled();
  const { t } = useTranslation();
  const { data: addressesData } = useAddresses();
  const { data: areasData } = useAreas();
  const addresses = Array.isArray(addressesData) ? addressesData : [];
  const areas = Array.isArray(areasData) ? areasData : [];
  const defaultAddress = addresses.find((a: any) => a.is_default) ?? addresses[0];

  const [selectedAddress, setSelectedAddress] = useState<any>(defaultAddress ?? null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const c = useThemeColors() ?? useThemeStore.getState().colors;
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const styles = useMemo(() => createOrderCustomerDetailsStyles(c), [c]);

  useEffect(() => {
    setSelectedAddress(defaultAddress ?? null);
  }, [defaultAddress]);

  useEffect(() => {
    let mounted = true;
    getBookMealData().then(stored => {
      if (!mounted) return;
      setLoading(false);
      if (stored && stored.length > 0) {
        setItems(
          stored.map((s: any) => ({
            id: s.id,
            name: s.name,
            description: s.description,
            attachment: s.attachment ?? s.image,
            image: s.attachment ?? s.image,
            price: s.price,
            quantity: s.quantity ?? 1,
            a_name: s.a_name,
            a_description: s.a_description,
          })),
        );
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  const deliveryFee = useMemo(() => {
    if (!selectedAddress?.area_id) return 0;
    const area = areas.find((a: any) => a.id === selectedAddress.area_id || a.id === selectedAddress.area?.id);
    const charge = area?.order_delivery_charge ?? area?.delivery_charge ?? 0;
    return Number(charge) || 0;
  }, [selectedAddress?.area_id, selectedAddress?.area?.id, areas]);

  const subtotal = useMemo(() => items.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0), [items]);

  const proceedToPay = () => {
    if (items.length === 0) return;
    navigation.navigate('OrderCheckout', {
      items,
      address: selectedAddress ?? undefined,
    });
  };
  const inset = useSafeAreaInsets();
  const content_top = androidAppHeaderContentTop(inset.top, appTheme.spacing.sm);
  const go_back = useCallback(() => navigation.goBack(), [navigation]);

  if (!online_orders_enabled) {
    return null;
  }

  if (loading) {
    return (
      <StackScreenShell colors={c}>
        <ScreenHeader title={t('order.customerDetails')} onBack={go_back} scrollY={scrollY} />
        <View style={styles.center}>
          <ActivityIndicator size="large" color={c.primary} />
        </View>
      </StackScreenShell>
    );
  }

  if (items.length === 0) {
    return (
      <StackScreenShell colors={c}>
        <ScreenHeader title={t('order.customerDetails')} onBack={go_back} scrollY={scrollY} />
        <View style={styles.center}>
          <Text style={styles.emptyText}>{t('order.noItemsInCart')}</Text>
          <Clickable
            style={{ alignSelf: 'stretch', width: '100%', maxWidth: 320 }}
            contentStyle={{ alignSelf: 'stretch' }}
            onPress={() => navigation.navigate('MainTabs', { screen: 'Order' })}
            accessibilityLabel={t('order.backToOrder')}
            accessibilityRole="button">
            <View style={styles.primaryBtnWrap}>
              <View style={styles.primaryBtn}>
                <Text style={styles.primaryBtnText}>{t('order.backToOrder')}</Text>
              </View>
            </View>
          </Clickable>
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={c}>
      <ScreenHeader title={t('order.customerDetails')} onBack={go_back} scrollY={scrollY} />
      <Animated.ScrollView
        style={styles.scroll}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.scrollContent, { paddingTop: content_top, paddingBottom: inset.bottom + 100 }]}>
        <Text style={styles.sectionTitle}>{t('order.selectDeliveryAddress')}</Text>
        {addresses.length === 0 ? (
          <Clickable
            style={styles.addressCardWrap}
            contentStyle={{ alignSelf: 'stretch' }}
            onPress={() => navigation.navigate('AddressLists')}
            accessibilityLabel={t('address.selectDeliveryAddress')}
            accessibilityRole="button">
            <View style={styles.addressCard}>
              <SvgIcon name="location-outline" size={24} color={c.primary} />
              <View style={styles.addressInfo}>
                <Text style={styles.addressName}>{t('address.selectDeliveryAddress')}</Text>
                <Text style={styles.addressSub}>{t('address.tapToAddAddress')}</Text>
              </View>
              <SvgIcon name="chevron-forward" size={20} color={c.textSecondary} />
            </View>
          </Clickable>
        ) : (
          addresses.map((addr: any) => {
            const isSelected = selectedAddress?.id === addr.id;
            return (
              <Clickable
                key={addr.id}
                style={styles.addressCardWrap}
                contentStyle={{ alignSelf: 'stretch' }}
                onPress={() => setSelectedAddress(addr)}
                accessibilityLabel={getApiText(addr, 'name', 'name_ar') || addr.name || t('address.addressFallback')}
                accessibilityRole="button">
                <View style={[styles.addressCard, isSelected && styles.addressCardSelected]}>
                  <View style={[styles.radioOuter, isSelected && styles.radioOuterSelected]}>{isSelected ? <View style={styles.radioInner} /> : null}</View>
                  <SvgIcon name="location-outline" size={22} color={c.primary} />
                  <View style={styles.addressInfo}>
                    <Text style={styles.addressName}>{getApiText(addr, 'name', 'name_ar') || addr.name || t('address.addressFallback')}</Text>
                    <Text style={styles.addressSub} numberOfLines={2}>
                      {addr.address || addr.full_address || addr.block || '—'}
                    </Text>
                  </View>
                </View>
              </Clickable>
            );
          })
        )}

        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('order.subtotal')}</Text>
            <Text style={styles.summaryValue}>{formatCurrency(subtotal)}</Text>
          </View>
          {deliveryFee > 0 && (
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>{t('order.deliveryFee')}</Text>
              <Text style={styles.summaryValue}>{formatCurrency(deliveryFee)}</Text>
            </View>
          )}
          <View style={styles.summaryRow}>
            <Text style={styles.totalLabel}>{t('order.total')}</Text>
            <Text style={styles.totalValue}>{formatCurrency(subtotal + deliveryFee)}</Text>
          </View>
        </View>

        <View style={styles.bottomSpacer} />
      </Animated.ScrollView>

      <View style={[styles.footer, { paddingBottom: Math.max(inset.bottom, appTheme.spacing.md) }]}>
        <Text style={styles.footerTotal}>{formatCurrency(subtotal + deliveryFee)}</Text>
        <Clickable
          onPress={proceedToPay}
          disabled={!selectedAddress}
          accessibilityLabel={t('checkout.proceedToPayment')}
          accessibilityRole="button"
          style={{ flex: 1, minWidth: 0, marginStart: appTheme.spacing.md }}
          contentStyle={{ alignSelf: 'stretch' }}>
          <View style={styles.primaryBtnWrap}>
            <View style={[styles.primaryBtn, !selectedAddress && styles.primaryBtnDisabled]}>
              <Text style={styles.primaryBtnText}>{t('checkout.proceedToPayment')}</Text>
              <SvgIcon name={trailingRowDisclosureChevron()} size={18} color={c.onPrimary} />
            </View>
          </View>
        </Clickable>
      </View>
    </StackScreenShell>
  );
};

export default OrderCustomerDetailsScreen;
